import { ObjectType } from "#graphql/supergraphEnums";
import { type CreatePaymentBatchInput } from "#graphql/supergraphTypes";
import { toDbId } from "#utils/graphql_utils";
import { paymentProcessDateToISO } from "../../../DistributionWorkflow/StepComponents/ReviewAndSubmitStep/utils";
import { stringToPaymentMethod } from "../../../DistributionWorkflow/utils";
import { PositionRowData } from "../../types";

export const stringToObjectType = (objectType: string): ObjectType | null => {
  const maybeObjectType = objectType.toUpperCase() as ObjectType;
  if (Object.values(ObjectType).includes(maybeObjectType)) {
    return maybeObjectType;
  }
  return null;
};

export const buildCreatePaymentBatchInput = (params: {
  selectedPositions: PositionRowData[];
  paymentDescription: string | undefined;
  batchId: string;
  batchObjectType: string;
  scheduledPaymentProcessDate: Date | null;
  makerNote?: string;
  bankAccountId: string | null;
}): CreatePaymentBatchInput => {
  const {
    selectedPositions,
    paymentDescription,
    batchId,
    batchObjectType,
    scheduledPaymentProcessDate,
    makerNote,
    bankAccountId,
  } = params;

  let scheduledPaymentProcessISODate: string | null = null;
  if (scheduledPaymentProcessDate) {
    scheduledPaymentProcessISODate = paymentProcessDateToISO(scheduledPaymentProcessDate);
  }

  const resolvedBatchObjectType = stringToObjectType(batchObjectType);
  if (!resolvedBatchObjectType) {
    throw new TypeError(`Invalid batchObjectType: ${batchObjectType}`);
  }

  const payments: CreatePaymentBatchInput["payments"] = selectedPositions.map((p) => {
    const paymentMethod = stringToPaymentMethod(p.paymentMethod);
    if (!paymentMethod) {
      // should never get here as we shouldn't be creating mutation inputs
      // for positions with undefined payment method
      throw new TypeError("paymentMethod is undefined or null");
    }
    if (p.paymentPrefId == null) {
      throw new TypeError("paymentPrefId is undefined or null");
    }
    return {
      objectId: p.instanceId,
      objectType: p.instanceObjectType as ObjectType,
      method: paymentMethod,
      amount: p.netPayment,
      positionId: p.positionId,
      paymentPrefId: p.paymentPrefId,
    };
  });

  return {
    objectId: toDbId(batchId),
    objectType: resolvedBatchObjectType,
    paymentDescription,
    payments,
    scheduledPaymentProcessDate: scheduledPaymentProcessISODate,
    makerNote: makerNote?.trim() || null,
    bankAccountId: bankAccountId ? toDbId(bankAccountId) : null,
  };
};
